<script setup lang="ts">
import { dayJs } from '~/composables/dayjs'
import { kebabCase } from 'lodash-es'

interface Recurrence {
  dateTimeStart: string
  dateTimeEnd: string
}

interface ProgrammeDoc {
  id: string
  title: string
  slug: string
  recurrences: Recurrence[]
  image?: Media | null
}

const props = defineProps<{
  programmes: ProgrammeDoc[]
  typeSlug: string
  viewOption?: 'title' | 'date' | 'grouped-by-year' | 'grouped-by-year-title-only' | 'thumbnails' | 'image'
}>()

// Normalize viewOption to kebab-case so template comparisons don't have to
// juggle multiple casing conventions.
const viewOption = computed(() => (
  props.viewOption
    ? kebabCase(props.viewOption) as 'title' | 'date' | 'grouped-by-year' | 'grouped-by-year-title-only' | 'thumbnails' | 'image'
    : undefined
))

const routeFor = (prog: ProgrammeDoc) => ({
  name: 'programme-type-slug',
  params: { type: props.typeSlug, slug: prog.slug },
})

const dateOf = (prog: ProgrammeDoc) => prog.recurrences?.[0]?.dateTimeStart

// "date" and the "grouped-by-year" views are inherently chronological, so they
// always show newest-first regardless of the sortOption applied upstream.
const chronologicalViews = ['date', 'grouped-by-year', 'grouped-by-year-title-only']
const orderedProgrammes = computed(() => {
  if (!viewOption.value || !chronologicalViews.includes(viewOption.value)) return props.programmes
  return [...props.programmes].sort(
    (a, b) => new Date(dateOf(b) ?? 0).getTime() - new Date(dateOf(a) ?? 0).getTime()
  )
})

// "By date": find the coarsest date part that still gives every item a distinct
// label, so entries don't repeat an identical-looking date (e.g. two different
// 2026 events both just labelled "2026"). Programmes without a recurrence date
// are skipped: dayjs.utc(undefined) would otherwise silently fall back to "now",
// corrupting both the uniqueness check below and the grouping in groupedByYear.
const dateLabels = computed(() => {
  const dayjs = dayJs()
  const entries = orderedProgrammes.value
    .filter(prog => dateOf(prog))
    .map(prog => ({ prog, date: dayjs.utc(dateOf(prog)) }))
  const dates = entries.map(({ date }) => date)

  const sameYear = dates.length > 0 && dates.every(d => d.isSame(dates[0], 'year'))
  const sameMonth = sameYear && dates.every(d => d.isSame(dates[0], 'month'))

  // Candidates ordered from most compact to most explicit. Year is only ever
  // dropped when every date already shares it, so dropping it can't cause a
  // same-year collision.
  const candidates = sameMonth
    ? ['D MMMM', 'D MMMM YYYY']
    : sameYear
      ? ['MMMM YYYY', 'D MMMM YYYY']
      : ['YYYY', 'MMMM YYYY', 'D MMMM YYYY']

  const format = candidates.find(candidate => {
    const labels = dates.map(d => d.format(candidate))
    return new Set(labels).size === labels.length
  }) ?? 'D MMMM YYYY'

  return new Map(entries.map(({ prog, date }) => [prog.id, date.format(format)]))
})

// "Grouped by year": cluster items by year, in descending date order.
const groupedByYear = computed(() => {
  const dayjs = dayJs()
  const groups = new Map<string, ProgrammeDoc[]>()
  for (const prog of orderedProgrammes.value) {
    const date = dateOf(prog)
    if (!date) continue
    const year = dayjs.utc(date).format('YYYY')
    if (!groups.has(year)) groups.set(year, [])
    groups.get(year)!.push(prog)
  }
  // Sort the years explicitly rather than leaning on Map insertion order: the
  // upstream sort can be non-chronological, and even ordered input would only
  // give insertion order by accident.
  return [...groups.entries()]
    .sort(([a], [b]) => Number(b) - Number(a))
    .map(([year, programmes]) => ({ year, programmes }))
})
</script>

<template>
  <ul 
    v-if="!viewOption || viewOption === 'title'" 
    class="flex flex-col gap-0_35"
  >
    <li v-for="prog in programmes" :key="prog.id" >
      <site-link 
        :route-object="routeFor(prog)"
        class="group"
      >
        <div class="text-accent group-hover:text-black">
          {{ prog.title }}
        </div>
        <div
          v-for="(recurrence, index) in getFormattedRecurrences(prog.recurrences)"
          :key="index"
        >
          {{ recurrence }}
        </div>
      </site-link>
    </li>
  </ul>

  <p v-else-if="viewOption === 'date'">
    <template v-for="(prog, index) in orderedProgrammes" :key="prog.id">
      <site-link 
        :route-object="routeFor(prog)" 
        :aria-label="prog.title"
        class="text-hover-accent"
      >
        {{ dateLabels.get(prog.id) }}
      </site-link><span v-if="index < orderedProgrammes.length - 1">, </span>
    </template>
  </p>

  <div
    v-else-if="viewOption === 'grouped-by-year'"
    class="columns-3 <md:columns-2 <sm:columns-1 gap-1 !-mb-1"
  >
    <div 
      v-for="group in groupedByYear" 
      :key="group.year"
      class="break-inside-avoid pb-1"
    >
      <div class="text-d">{{ group.year }}</div>
      <ul>
        <li v-for="prog in group.programmes" :key="prog.id" class="text-d">
          <site-link :route-object="routeFor(prog)" class="group">
            <div class="text-accent group-hover:text-black">{{ prog.title }}</div>
            <div
              v-for="(recurrence, index) in getFormattedRecurrences(prog.recurrences)"
              :key="index"
            >
              {{ recurrence }}
            </div>
          </site-link>
        </li>
      </ul>
    </div>
  </div>

  <div
    v-else-if="viewOption === 'grouped-by-year-title-only'"
    class="columns-3 <md:columns-2 <sm:columns-1 gap-1 !-mb-1"
  >
    <div 
      v-for="group in groupedByYear" 
      :key="group.year"
      class="break-inside-avoid pb-1"
    >
      <div class="text-d">{{ group.year }}</div>
      <ul>
        <li v-for="prog in group.programmes" :key="prog.id" class="text-d">
          {{ prog.title }}
        </li>
      </ul>
    </div>
  </div>

  <ul 
    v-else-if="viewOption === 'thumbnails'" 
    class="flex flex-col gap-2 container-d"
  >
    <li v-for="prog in programmes" :key="prog.id">
      <site-link :route-object="routeFor(prog)" class="block group">
        <core-image 
          v-if="prog.image" 
          :image="prog.image" 
          class="mb-0_35 group-hover:opacity-75"
        />

        <div class="text-d">
          <div class="text-accent group-hover:text-black">
            {{ prog.title }}
          </div>

          <span
            v-for="(recurrence, index) in getFormattedRecurrences(prog.recurrences)"
            :key="index"
            class="block"
          >
            {{ recurrence }}
          </span>
        </div>

        <div
          v-if="hasRichTextContent(prog?.description)"
          class="mt-0_35"
        >
          <core-rich-text :content="prog.description" text-size-class="text-d" />
        </div>
      </site-link>
    </li>
  </ul>

  <div 
    v-else-if="viewOption === 'image'"
    v-if="programmes?.filter(prog => prog?.image?.id)?.map(prog => prog?.image?.id)?.length"
  >
    <core-media-preview
      :media="programmes?.filter(prog => prog?.image?.id)?.map(prog => prog?.image)"
      :limit="4"
    />
  </div>
</template>
